import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, Subject} from 'rxjs';
import {map} from 'rxjs/operators';
import {FileDeletionBatchDTO, ProjectFile, ProjectFileControllerService} from '../../api';
import {BackgroundProgress, mergeProgress} from '../background-progress.model';
import {FileDeletionProgressEvent} from '../sse/sse-event.model';
import {NotificationsService} from '../notifications/notifications.service';
import {StateConfigService} from '../state/state-config.service';

export type DeleteJobState = 'queued' | 'deleting' | 'done' | 'error';

export interface DeleteJob {
  clientId: string;
  /** Klammert alle Dateien EINER Löschaktion zusammen (Einzelzeile oder Mehrfachauswahl). */
  batchId: string;
  projectId: number;
  fileId: string;
  name: string;
  isFolder: boolean;
  createdAt: number;

  state: DeleteJobState;
  /** Wann der Server die Löschung bestätigt bzw. abgelehnt hat - Grundlage für die Dauer-Anzeige. */
  finishedAt?: number;
  error?: unknown;
}

/**
 * Zentraler Lösch-Service.
 *
 * Die Warteschlange liegt seit 2026-09-04 auf dem SERVER. Vorher verschickte dieser Service einen
 * DELETE je Datei und hielt den Rest im Tab: ein Reload verwarf alles noch Wartende still, die zwei
 * fliegenden Requests liefen durch, und der Nutzer sah danach die halbe Auswahl wieder da. Jetzt legt
 * ein Aufruf einen Batch an, den der Server abarbeitet — weiterhin mit einer Transaktion je Datei,
 * damit ein Reload einen konsistenten Teilstand zeigt und eine einzelne fehlgeschlagene Datei nicht
 * die ganze Auswahl mitreißt.
 *
 * Was dieser Service dadurch NICHT mehr macht: parallelisieren, Ordner nach Dateien einsortieren,
 * 404 als Erfolg auslegen. Das steckt jetzt im `FileDeletionBatchService` — die Reihenfolge
 * "Dateien vor Ordnern" ebenso, weil der Server einen Ordner rekursiv mitlöscht.
 *
 * Zwei Zustandsquellen mit klarer Aufteilung:
 * - **jobs$** — lokale Jobs für Batches, die DIESER Tab gestartet hat. Sie tragen Namen und Datei-IDs
 *   und speisen den Fortschritts-Dialog sowie das Entfernen der Tabellenzeilen. Ihre Zustände kommen
 *   per SSE, nicht mehr aus HTTP-Antworten.
 * - **progress$** — die Zähler des Servers. Sie gelten auch für einen Batch, den ein anderer Tab
 *   gestartet hat oder der diesen Reload überlebt hat: dafür gibt es hier keine Jobs, die Badge soll
 *   ihn aber trotzdem zeigen.
 */
@Injectable({providedIn: 'root'})
export class DeleteService {
  private readonly fileController = inject(ProjectFileControllerService);
  private readonly stateConfigService = inject(StateConfigService);
  private readonly notificationsService = inject(NotificationsService);

  private readonly jobsSubject = new BehaviorSubject<DeleteJob[]>([]);
  public readonly jobs$ = this.jobsSubject.asObservable();

  private readonly jobUpdatesSubject = new Subject<DeleteJob>();
  public readonly jobUpdates$ = this.jobUpdatesSubject.asObservable();

  /** Serverstand der offenen Batches, nach der Batch-ID DES SERVERS. */
  private readonly batchesSubject = new BehaviorSubject<Map<string, FileDeletionBatchDTO>>(new Map());

  /**
   * Server-Batch-ID -> lokale Batch-ID.
   *
   * Die lokale ID bleibt bewusst die kanonische für die Jobs: Aufrufer klammern ihre Aktion damit
   * (Fortschritts-Dialog, Filter auf {@link jobUpdates$}) und tun das, bevor die Serverantwort da
   * ist. Würden die Jobs nachträglich auf die Server-ID umgeschrieben, verlöre ein bereits
   * laufender Filter seine Treffer. Also wird nur übersetzt.
   */
  private readonly serverToLocalBatchId = new Map<string, string>();

  /**
   * Fortschritt für die Badge, EIN Eintrag JE PROJEKT — siehe {@link BackgroundProgress}.
   *
   * Je Projekt, weil die Warteschlange tabweit ist: eine Löschung läuft beim Projektwechsel weiter,
   * und danach kann in einem anderen Projekt eine zweite starten. Eine gemeinsame Zeile hätte die
   * Zähler vermischt und beim Klick kein eindeutiges Ziel mehr gehabt.
   *
   * Zwei Quellen, addiert und ohne Doppelzählung:
   * - die **lokalen Jobs** für Aktionen, die dieser Tab gestartet hat. Sie stehen sofort zur
   *   Verfügung — vor der Antwort auf `createDeletionBatch` — und sind der Grund, warum der
   *   Fortschritt nicht erst am Ende erscheint.
   * - die **Server-Zähler** nur für Batches OHNE lokale Jobs: fremde Tabs und alles, was per
   *   {@link resumeOpenBatches} nach einem Reload aufgegriffen wurde.
   */
  public readonly progressByProject$: Observable<BackgroundProgress[]> = combineLatest([
    this.jobs$, this.batchesSubject,
  ]).pipe(
    map(([jobs, batches]) => {
      const foreign = Array.from(batches.entries())
        .filter(([serverBatchId]) => !this.serverToLocalBatchId.has(serverBatchId))
        .map(([, dto]) => dto);

      // Beide Quellen in dieselben Toepfe. Ein Stapel ohne projectId ist nur theoretisch moeglich
      // (das Feld ist im DTO optional) - er landet beim ausgewaehlten Projekt, das ist die einzige
      // Vermutung, die sich hier treffen laesst.
      const projectIds = new Set<number>([
        ...jobs.map(j => j.projectId),
        ...foreign.map(b => b.projectId ?? this.stateConfigService.getProjectId()),
      ]);

      return Array.from(projectIds).map(projectId => {
        const own = jobs.filter(j => j.projectId === projectId);
        const others = foreign.filter(
          b => (b.projectId ?? this.stateConfigService.getProjectId()) === projectId);

        return {
          total: own.length + others.reduce((sum, b) => sum + (b.total ?? 0), 0),
          done: own.filter(j => j.state === 'done').length
            + others.reduce((sum, b) => sum + (b.done ?? 0), 0),
          failed: own.filter(j => j.state === 'error').length
            + others.reduce((sum, b) => sum + (b.failed ?? 0), 0),
          active: own.filter(j => j.state === 'queued' || j.state === 'deleting').length
            + others.reduce((sum, b) => sum + (b.active ?? 0), 0),
          currentNames: [
            ...own.filter(j => j.state === 'deleting').map(j => j.name),
            ...others.flatMap(b => b.currentNames ?? []),
          ],
          failedNames: [
            ...own.filter(j => j.state === 'error').map(j => j.name),
            ...others.flatMap(b => b.failedNames ?? []),
          ],
          projectId,
        };
      });
    }),
  );

  /** Tabweite Zusammenfassung - fuer alles, was nicht je Projekt gedacht ist. */
  public readonly progress$: Observable<BackgroundProgress> = this.progressByProject$.pipe(
    map(mergeProgress),
  );

  /** True, sobald mindestens eine Löschung offen ist. */
  public readonly hasActiveDeletes$: Observable<boolean> = this.progress$.pipe(
    map(progress => progress.active > 0),
  );

  constructor() {
    // Der SSE-Strom läuft zentral im StateConfigService; hier wird nur mitgehört. Eine zweite
    // Verbindung für dieselben Ereignisse wäre Verschwendung.
    this.stateConfigService.sseEvents$.subscribe(event => {
      if (event.type === 'FILE_DELETION_PROGRESS') {
        this.applyProgressEvent(event.data as FileDeletionProgressEvent);
      } else if (event.type === 'FILE_DELETION_COMPLETED') {
        this.applyCompletion(event.data as string);
      }
    });

    // Nach einer SSE-Unterbrechung aufholen: der Strom hat kein Nachholen, in das Fenster gefallene
    // Meldungen sind weg. Ohne das blieben Zeilen dauerhaft auf "wird geloescht" stehen - genau die
    // "4 Dateien haengen noch, obwohl alles weg ist"-Beobachtung.
    this.stateConfigService.sseReconnects$.subscribe(() => this.reconcileAfterReconnect());
  }

  /**
   * Gleicht den lokalen Stand nach einer SSE-Unterbrechung gegen den Server ab.
   *
   * Zwei Dinge werden korrigiert: die Zaehler der verfolgten Stapel, und - wichtiger - die einzelnen
   * Zeilen. Was der Server nicht mehr als offen fuehrt, ist durch; die Meldung dazu ist verloren
   * gegangen. Ein Stapel, der waehrend der Unterbrechung ganz fertig geworden ist, taucht in der
   * Antwort gar nicht mehr auf - dann ist die Menge der offenen IDs leer und alle Zeilen gehen auf
   * "fertig", was genau richtig ist.
   *
   * Laeuft nur, wenn ueberhaupt etwas offen ist: im Ruhezustand waere es ein Request alle paar
   * Minuten fuer nichts.
   */
  private reconcileAfterReconnect(): void {
    const openJobs = this.jobsSubject.value
      .filter(job => job.state === 'queued' || job.state === 'deleting');
    if (openJobs.length === 0 && this.batchesSubject.value.size === 0) return;

    const projectId = this.stateConfigService.getProjectId();
    this.fileController.getActiveDeletionBatches(projectId).subscribe({
      next: batches => {
        const reported = new Set(batches.map(batch => batch.batchId));
        // Verfolgte Stapel desselben Projekts, die der Server nicht mehr als offen fuehrt, sind
        // fertig. Nur dieses Projekt, damit ein Stapel eines anderen Projekts nicht mitgeloescht wird.
        const stale = Array.from(this.batchesSubject.value.values())
          .filter(batch => batch.projectId === projectId && !reported.has(batch.batchId));
        if (stale.length > 0) {
          const remaining = new Map(this.batchesSubject.value);
          stale.forEach(batch => remaining.delete(batch.batchId!));
          this.batchesSubject.next(remaining);
        }
        batches.forEach(batch => this.upsertBatch(batch));

        const stillOpen = new Set(batches.flatMap(batch => batch.openFileIds ?? []));
        for (const job of openJobs) {
          if (job.fileId && !stillOpen.has(job.fileId)) {
            this.setJob(job.clientId, {state: 'done', finishedAt: Date.now()});
          }
        }
      },
      error: () => { /* Best-Effort: der naechste Abgleich holt es nach. */ },
    });
  }

  /** Jobs einer Löschaktion - in Auswahlreihenfolge, damit die Liste im Dialog nicht springt. */
  public jobsForBatch$(batchId: string): Observable<DeleteJob[]> {
    return this.jobs$.pipe(
      map(jobs => jobs.filter(j => j.batchId === batchId).sort((a, b) => a.createdAt - b.createdAt)),
    );
  }

  /**
   * Startet eine Löschaktion auf dem Server.
   *
   * Die lokalen Jobs entstehen SOFORT, bevor die Antwort da ist: der Fortschritts-Dialog öffnet
   * unmittelbar nach dem Klick und soll nicht erst leer stehen. Kommt die Antwort, wird die
   * Server-Batch-ID nur als Übersetzung vermerkt (siehe {@link serverToLocalBatchId}) — die Jobs
   * behalten ihre lokale ID, damit bereits gesetzte Filter weiter greifen.
   *
   * @return Die lokalen Jobs.
   */
  public enqueueDeletes(projectId: number, files: ProjectFile[], batchId: string = this.createBatchId()): DeleteJob[] {
    const now = Date.now();

    // Dateien, die schon in einer OFFENEN Loeschaktion stecken, nicht erneut einreihen.
    // Die Tabellenzeile verschwindet erst, wenn der Server die Loeschung bestaetigt hat - bis dahin
    // kann man dieselbe Auswahl erneut auf "Loeschen" schicken. Ohne diesen Filter entstanden dabei
    // zweite Jobs fuer dieselben Dateien: die Anzeige sprang auf "0 von 600", und der Server bekam
    // einen zweiten Batch fuer Dateien, die der erste gerade abarbeitet.
    const alreadyRunning = new Set(
      this.jobsSubject.value
        .filter(job => job.state === 'queued' || job.state === 'deleting')
        .map(job => job.fileId),
    );

    const newJobs: DeleteJob[] = files
      .filter(file => !!file.id && !alreadyRunning.has(file.id!))
      .map((file, idx) => ({
        clientId: `${projectId}-${now}-${idx}-${Math.random().toString(16).slice(2)}`,
        batchId,
        projectId,
        fileId: file.id!,
        name: file.name ?? '',
        isFolder: !!file.isFolder,
        createdAt: now + idx,
        state: 'queued' as DeleteJobState,
      }));

    if (newJobs.length === 0) {
      // Alles schon in Arbeit - kein zweiter Batch, kein zweiter Zaehler.
      return [];
    }

    this.jobsSubject.next([...newJobs, ...this.jobsSubject.value]);

    // Papierkorb statt Loeschstapel: der Server setzt nur `deletedAt` (ein Bulk-Update), hart
    // geloescht wird spaeter von einem Aufraeumjob. Vorher lief hier ein serverseitiger Stapel, der
    // jede Datei einzeln durch den vollen Loeschpfad schickte - gemessen ~25 SQL-Statements je Datei
    // und rund 15 Minuten fuer 431 Dateien. Die ganze Maschinerie ringsherum (Fortschritt je Datei
    // ueber SSE, Nachholen verlorener Meldungen, Sweeper fuer unterbrochene Laeufe) existierte nur,
    // weil die Operation so lange dauerte.
    this.fileController.moveFilesToTrash(newJobs.map(job => job.fileId), projectId).subscribe({
      next: () => {
        // Ein Request, ein Ergebnis: sobald er durch ist, sind alle Dateien dieser Aktion weg. Es
        // gibt keinen Zwischenstand mehr, den man melden koennte - und keinen mehr, auf den die
        // Anzeige warten muesste.
        const finishedAt = Date.now();
        for (const job of this.jobsSubject.value.filter(j => j.batchId === batchId)) {
          this.setJob(job.clientId, {state: 'done', finishedAt});
        }
      },
      error: err => {
        // 409 ist keine Störung, sondern eine bewusste Ablehnung: in die Auswahl wird noch
        // hochgeladen. Der Server ist hier die verlässliche Instanz - die Sperre in der Ansicht
        // kann nur prüfen, was sie geladen hat.
        if (err?.status === 409) {
          const count = Number(err?.headers?.get?.('X-Uploading-Count')) || 0;
          this.notificationsService.showErrorMessage(
            count > 0
              ? `${count} Datei${count !== 1 ? 'en' : ''} in dieser Auswahl ${count !== 1 ? 'werden' : 'wird'} `
                + 'noch hochgeladen. Bitte warten Sie, bis der Upload abgeschlossen ist.'
              : 'In dieser Auswahl wird noch hochgeladen. Bitte warten Sie, bis der Upload '
                + 'abgeschlossen ist.',
          );
        }

        // Alle Jobs dieser Aktion als Fehler markieren, sonst stehen sie für immer auf "queued" und
        // der Dialog wartet auf einen Abschluss, der nie kommt.
        for (const job of this.jobsSubject.value.filter(j => j.batchId === batchId)) {
          this.setJob(job.clientId, {state: 'error', finishedAt: Date.now(), error: err});
        }
      },
    });

    return newJobs;
  }

  /**
   * Holt offene Löschaktionen des Nutzers vom Server.
   *
   * Damit greift die Badge nach einem Reload oder in einem zweiten Tab wieder auf einen laufenden
   * Batch auf - genau der Fall, den die frühere clientseitige Warteschlange nicht konnte. Bewusst
   * ohne lokale Jobs: die Tabelle wurde beim Laden ohnehin frisch vom Server geholt, bereits
   * gelöschte Zeilen sind also gar nicht mehr da. Gebraucht werden nur die Zähler.
   */
  public resumeOpenBatches(projectId: number): void {
    this.fileController.getActiveDeletionBatches(projectId).subscribe({
      next: batches => batches.forEach(batch => this.upsertBatch(batch)),
      error: () => { /* Best-Effort: ohne die Zähler fehlt nur die Anzeige, nicht die Löschung. */ },
    });
  }

  public createBatchId(): string {
    return `delete-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  /** Entfernt abgeschlossene Jobs, damit eine neue Löschaktion mit leerer Liste startet. */
  public clearFinished(): void {
    this.jobsSubject.next(
      this.jobsSubject.value.filter(j => j.state !== 'done' && j.state !== 'error'),
    );
  }

  /**
   * Übernimmt die Zustandsänderung EINER Datei.
   *
   * Ersetzt die frühere HTTP-Antwort je Datei. Die Datei-ID im Ereignis ist der Grund, warum das ohne
   * Nachladen funktioniert: die betroffene Zeile lässt sich direkt zuordnen.
   */
  private applyProgressEvent(event: FileDeletionProgressEvent): void {
    if (!event?.batchId) return;

    // Zaehler nur fuer Batches fortschreiben, zu denen dieser Tab KEINE lokalen Jobs hat (also
    // fremde oder per resumeOpenBatches aufgegriffene): fuer die eigenen liefert progress$ die
    // Zahlen ohnehin aus den Jobs, beides zusammen wuerde doppelt zaehlen.
    const batches = new Map(this.batchesSubject.value);
    const batch = event.state === 'DELETING' || this.serverToLocalBatchId.has(event.batchId)
      ? undefined
      : batches.get(event.batchId);
    if (batch) {
      const failed = event.state === 'FAILED';
      batches.set(event.batchId, {
        ...batch,
        done: (batch.done ?? 0) + (failed ? 0 : 1),
        failed: (batch.failed ?? 0) + (failed ? 1 : 0),
        active: Math.max(0, (batch.active ?? 0) - 1),
      });
      this.batchesSubject.next(batches);
    }

    // Zuordnung ueber die DATEI-ID, nicht ueber die Batch-ID: die Server-Batch-ID kennt dieser
    // Client erst aus der Antwort auf createDeletionBatch, die Fortschrittsmeldungen kommen aber
    // schon vorher. Ueber die Batch-ID zugeordnet wurde deshalb JEDE fruehe Meldung verworfen - der
    // Dialog blieb auf 0 stehen, obwohl der Server laengst loeschte. Eine Datei kann nur in einer
    // laufenden Loeschaktion stecken, also ist die Datei-ID hier eindeutig.
    const job = this.jobsSubject.value
      .find(j => j.fileId === event.fileId && (j.state === 'queued' || j.state === 'deleting'));
    if (job) {
      if (event.state === 'DELETING') {
        this.setJob(job.clientId, {state: 'deleting'});
      } else {
        this.setJob(job.clientId, {
          state: event.state === 'FAILED' ? 'error' : 'done',
          finishedAt: Date.now(),
        });
      }
    }
  }

  /**
   * Schließt einen Batch ab.
   *
   * Offene Jobs werden dabei aufgeräumt: geht eine Fortschrittsmeldung verloren (SSE-Abbruch während
   * eines Deployments), bliebe die Datei sonst für immer auf "queued" stehen und der Dialog käme nie
   * zum Ende. Der Server ist mit diesem Ereignis fertig, also sind sie es auch.
   */
  private applyCompletion(batchId: string): void {
    if (!batchId) return;

    const localBatchId = this.serverToLocalBatchId.get(batchId);
    if (localBatchId !== undefined) {
      for (const job of this.jobsSubject.value) {
        if (job.batchId === localBatchId && (job.state === 'queued' || job.state === 'deleting')) {
          this.setJob(job.clientId, {state: 'done', finishedAt: Date.now()});
        }
      }
      this.serverToLocalBatchId.delete(batchId);
    }

    const batches = new Map(this.batchesSubject.value);
    batches.delete(batchId);
    this.batchesSubject.next(batches);
  }


  private upsertBatch(batch: FileDeletionBatchDTO): void {
    if (!batch.batchId) return;

    const batches = new Map(this.batchesSubject.value);
    if (batch.completedAt) {
      batches.delete(batch.batchId);
    } else {
      batches.set(batch.batchId, batch);
    }
    this.batchesSubject.next(batches);
  }

  private setJob(clientId: string, patch: Partial<DeleteJob>): void {
    const jobs = this.jobsSubject.value;
    const idx = jobs.findIndex(j => j.clientId === clientId);
    if (idx === -1) return;

    const updated: DeleteJob = {...jobs[idx], ...patch};
    const next = [...jobs];
    next[idx] = updated;
    this.jobsSubject.next(next);
    this.jobUpdatesSubject.next(updated);
  }
}
